type Orientation = 'horizontal' | 'vertical'
import { Favorite, MoreVertIcon } from '@tunasong/icons'
import { capitalize, dayjs, isAIGenerated, isFacade, isPersistedEntity, SystemTags } from '@tunasong/models'
import type { RenderMenuItemProps } from '@tunasong/plugin-lib'
import { isEntity, isPersisted } from '@tunasong/schemas'
import type { FC, MouseEvent } from 'react'
import React, { useCallback, useState } from 'react'
import { useDeviceInfo } from '../browser/device.hook.js'
import { useUIExtensions } from '../extensions.hook.js'
import { useMergeRefs } from '../hooks/merge-refs.js'
import { TunaEntityBreadcrumbs } from '../navigation/entity-breadcrumbs.js'
import { UserAvatar } from '../profile/index.js'
import { TagList } from '../tags/tag-list.js'
import { useCurrentUser } from '@tunasong/redux'
import { AIGeneratedBadge } from './ai-generated-badge.js'
import { EntityIcon } from './entity-icon.js'
import { EntityListItemFacade } from './entity-list-item-facade.js'
import { StyledEntityAvatar, StyledListItemAvatar, StyledListItemText } from './list-item.styles.js'
import { usePreloadOnHover } from './preload-on-hover.js'
import { IconButton, ListItemIcon, ListItem, type ListItemProps, ListItemButton } from '../primitives/index.js'
import styles from './entity-list-item.module.css'

/** Pick only the ListItem-specific props to avoid HTMLAttributes<HTMLLIElement> vs HTMLAttributes<HTMLDivElement> conflicts with RenderMenuItemProps */
type ListItemOwnProps = Pick<
  ListItemProps,
  'dense' | 'button' | 'selected' | 'disabled' | 'disablePadding' | 'disableGutters' | 'secondaryAction' | 'alignItems'
>

export interface EntityListItemProps extends RenderMenuItemProps, ListItemOwnProps {
  /** Mark the item as a favorite */
  favorite?: boolean
  size?: 'tiny' | 'small' | 'large' | 'medium'
  hideSubtitle?: boolean
  orientation?: Orientation
  iconStyle?: 'avatar' | 'icon'
  showBreadcrumbs?: boolean | 'inactiveLinks'
  ref?: React.Ref<HTMLLIElement>
  onClick?(ev: MouseEvent): void
}

export const EntityListItem: FC<EntityListItemProps> = props => {
  const {
    className,
    favorite,
    orientation = 'horizontal',
    element,
    size,
    hideSubtitle = false,
    iconStyle = 'icon',
    actions = true,
    showBreadcrumbs = false,
    onClick,
    ref,
    ...restProps
  } = props

  const me = useCurrentUser()

  const { isMobile } = useDeviceInfo()

  // If we have a plugin, we can show the item enabled

  const disabled = props.disabled // || !hasPlugin

  const [localRef, setLocalRef] = useState<HTMLLIElement | null>(null)
  const mergedRef = useMergeRefs(setLocalRef, ref)

  usePreloadOnHover({ entityId: element?.id, containerEl: localRef })

  const uiExtensions = useUIExtensions()

  const label = element.name
  // Phase 2 dashboard pivot: surface the *last editor* not the owner. Falls back to
  // owner when `lastEditedBy` is absent (legacy entities / search docs indexed before
  // the field landed).
  const lastEditorId = isPersisted(element) ? (element.lastEditedBy ?? element.userId) : undefined
  const hasExternalLastEditor = lastEditorId !== undefined && lastEditorId !== me.userId
  // Strip the AI-marker tags from the chip list — the AIGeneratedBadge carries
  // the signal instead. Both 'sys:ai-generated' (canonical) and the legacy
  // 'tunabrain' tag are filtered to cover historical data.
  const visibleTags = (element.tags ?? []).filter(t => t !== SystemTags.AIGenerated && t !== 'tunabrain')
  const showAIBadge = isPersistedEntity(element) && isAIGenerated(element)
  const subtitle =
    isPersistedEntity(element) && !hideSubtitle ? (
      <>
        {capitalize(element.type)}. Updated: {dayjs(element.updatedAt).fromNow()}
        {element.description ? ' - ' + element.description : ''}
        {showAIBadge ? <AIGeneratedBadge size="sm" /> : null}
        <TagList style={{ display: 'block', marginLeft: 8 }} tags={visibleTags} size="small" />
      </>
    ) : null

  /** Actions */
  const [menuAnchorEl, setAnchorEl] = React.useState<Element | null>(null)
  const handleActionsClose = useCallback(() => setAnchorEl(null), [])
  const handleActions = useCallback((ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault()
    ev.stopPropagation()
    setAnchorEl(el => (Boolean(el) ? null : ev.currentTarget))
  }, [])

  const title = disabled ? `No plugin for ${element.type}` : undefined

  const showMenuAction = !isMobile && isPersisted(element)

  const handleContext = useCallback((ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    setAnchorEl(el => (Boolean(el) ? null : ev.currentTarget))
  }, [])

  if (!(isEntity(element) && isPersisted(element))) {
    return null
  }

  const rightPadding = isMobile ? 0 : hasExternalLastEditor ? 12 : 8

  return isFacade(element) ? (
    <EntityListItemFacade
      className={className}
      element={element}
      {...restProps}
      ref={mergedRef as React.Ref<HTMLDivElement>}
      /** Override disabled if we have no plugin here */
      disabled={disabled}
    />
  ) : (
    <ListItem
      {...(restProps as Omit<typeof restProps, keyof React.DOMAttributes<HTMLDivElement>>)}
      ref={mergedRef as React.Ref<HTMLLIElement>}
      tabIndex={0}
      className={className}
      disabled={disabled}
      style={{
        padding: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      title={title}
      data-orientation={orientation}
      onContextMenu={handleContext as React.MouseEventHandler<HTMLLIElement>}
      secondaryAction={
        <div className={styles.secondaryAction}>
          {favorite ? (
            <span title="You have this as a favorite">
              <Favorite color="var(--tuna-palette-error-main)" />
            </span>
          ) : null}
          {actions && !disabled ? (
            <>
              {hasExternalLastEditor && lastEditorId ? <UserAvatar userId={lastEditorId} size="tiny" /> : null}
              {showMenuAction ? (
                <>
                  <IconButton tabIndex={-1} color="inherit" aria-label="settings" onClick={handleActions}>
                    <MoreVertIcon />
                  </IconButton>
                </>
              ) : null}
              {/* Expensive component so we run it on demand */}
              {menuAnchorEl && uiExtensions.entityList?.ItemMenu ? (
                <uiExtensions.entityList.ItemMenu
                  entity={element}
                  // clickAway will close the context menu on mobile
                  clickAway={!isMobile}
                  anchorEl={menuAnchorEl}
                  onClose={handleActionsClose}
                />
              ) : null}
            </>
          ) : null}
        </div>
      }
    >
      <ListItemButton
        disableRipple={true}
        className={styles.listItemButton}
        onClick={disabled ? undefined : (onClick as React.MouseEventHandler<HTMLDivElement>)}
      >
        {iconStyle === 'avatar' ? (
          <StyledListItemAvatar size={size}>
            <StyledEntityAvatar type={element.type} size={size} />
          </StyledListItemAvatar>
        ) : null}
        {iconStyle === 'icon' ? (
          <ListItemIcon
            style={{
              justifyContent: orientation === 'vertical' ? 'center' : undefined,
            }}
          >
            <EntityIcon entity={element} />
          </ListItemIcon>
        ) : null}

        <StyledListItemText
          style={{
            paddingRight: orientation === 'vertical' ? 0 : rightPadding,
          }}
          primary={
            <>
              {label}
              {showBreadcrumbs ? (
                <TunaEntityBreadcrumbs
                  entity={element}
                  showHome={false}
                  activeLinks={showBreadcrumbs === 'inactiveLinks' ? false : showBreadcrumbs}
                />
              ) : null}
            </>
          }
          secondary={subtitle}
        />
      </ListItemButton>
    </ListItem>
  )
}
